import {
  BottomSheet,
  Illustration,
  Text,
  TextButton,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Link } from "react-router-dom";

import { type PlacementIncludedWorkplace } from "../types/fetchPlacements.schema";

interface InterviewTodayRescheduleBottomSheetProps {
  isProspectWorkplace: boolean;
  modalState: UseModalState;
  workplaceReliabilityBanRule: NonNullable<
    NonNullable<PlacementIncludedWorkplace["meta"]>["reliabilityBanRule"]
  >;
  interviewId: string;
  onCancel: () => void;
  onReschedule: () => void;
}

export function InterviewTodayRescheduleBottomSheet(
  props: InterviewTodayRescheduleBottomSheetProps
) {
  const {
    isProspectWorkplace,
    modalState,
    workplaceReliabilityBanRule,
    onCancel,
    onReschedule,
    interviewId,
  } = props;

  const rescheduleLabel = isProspectWorkplace ? "walk-in interview" : "interview";

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_TODAY_RESCHEDULE_MODAL_VIEWED, {
    interviewId,
  });

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => {
              logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_TODAY_RESCHEDULE_MODAL_ACTIONS, {
                interviewId,
                action: "reschedule",
              });
              onReschedule();
            }}
          >
            Reschedule for today (no penalty)
          </Button>
          <TextButton
            LinkComponent={Link}
            size="large"
            sx={{ mt: 1, mb: 3 }}
            onClick={() => {
              logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_TODAY_RESCHEDULE_MODAL_ACTIONS, {
                interviewId,
                action: "cancel",
              });
              onCancel();
            }}
          >
            Cancel
          </TextButton>
        </DialogFooter>
      }
    >
      <IconButton
        size="small"
        iconType="close"
        variant="outlined"
        sx={{ position: "absolute", top: 24, right: 16 }}
        onClick={modalState.closeModal}
      />
      <Stack gap={7} px={7} pb={8} pt="76px" alignItems="center" justifyContent="center">
        <Illustration type="calendar-alert" />
        <Text variant="h2" textAlign="center">
          Reschedule your {rescheduleLabel}?
        </Text>
        <Text textAlign="center" color={(theme) => theme.palette.text.secondary}>
          It&apos;s too late to reschedule your interview for another day. If you cancel or no-show,
          you&apos;ll be restricted from viewing job openings for{" "}
          {workplaceReliabilityBanRule.restrictedDays} days.
        </Text>
      </Stack>
    </BottomSheet>
  );
}
